import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notesService } from "notes/services";
import { INote, INoteLike, INoteLikeSummary } from "notes/models";
import { useBoundStore } from "store";

export const notesKeys = {
  all: ["notes"] as const,
  list: (tenantId?: string, organizationId?: string, projectId?: string) =>
    [...notesKeys.all, tenantId ?? "unknown", organizationId ?? "unknown", projectId ?? "all"] as const,
};

/**
 * Fetches paginated notes for an organization, optionally filtered by project.
 * Uses infinite query for cursor-based pagination.
 */
export function useNotes(projectId?: string, options?: { enabled?: boolean }) {
  const { tenant, currentOrganization } = useBoundStore();

  return useInfiniteQuery({
    queryKey: notesKeys.list(tenant?.id, currentOrganization?.id, projectId),
    queryFn: ({ pageParam }) =>
      notesService.getNotes(tenant!.id, currentOrganization!.id, projectId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage?.lastEvaluatedKey ?? undefined,
    enabled: !!tenant?.id && !!currentOrganization?.id && (options?.enabled ?? true),
    staleTime: 5 * 60_000,
  });
}

/**
 * Mutation to dismiss/archive a note for the current user.
 * Invalidates notes queries on success so the note disappears from the dashboard.
 */
export function useDismissNoteMutation() {
  const queryClient = useQueryClient();
  const { tenant, currentOrganization } = useBoundStore();

  return useMutation({
    mutationFn: (noteId: string) =>
      notesService.dismissNote(tenant!.id, currentOrganization!.id, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notesKeys.all });
    },
  });
}

type NotesPage = { items: INote[]; lastEvaluatedKey?: string };
type NotesInfiniteData = { pages: NotesPage[]; pageParams: unknown[] };

/**
 * Rewrites one note wherever it appears in the cached notes pages. Every notes
 * query — the dashboard panel, the drawer, and the project detail list — is
 * built from the same `notesKeys.all` prefix, so a like made in one view lands
 * in all of them without a refetch.
 */
const patchCachedNote = (
  queryClient: ReturnType<typeof useQueryClient>,
  noteId: string,
  patch: (note: INote) => INote
) => {
  queryClient.setQueriesData<NotesInfiniteData>(
    { queryKey: notesKeys.all },
    (cached) => {
      if (!cached?.pages) return cached;
      return {
        ...cached,
        pages: cached.pages.map((page) => ({
          ...page,
          items: page.items?.map((note) => (note.id === noteId ? patch(note) : note)) ?? [],
        })),
      };
    }
  );
};

/**
 * Toggles the current user's like on a note.
 *
 * The cache is updated optimistically so the thumbs-up responds immediately,
 * then reconciled with the count and liker list the server returns. A failure
 * rolls the cache back to the snapshot taken before the click.
 */
export function useToggleNoteLikeMutation() {
  const queryClient = useQueryClient();
  const { tenant, currentOrganization, user } = useBoundStore();

  return useMutation({
    mutationFn: ({ noteId, liked }: { noteId: string; liked: boolean }) =>
      liked
        ? notesService.unlikeNote(tenant!.id, currentOrganization!.id, noteId)
        : notesService.likeNote(tenant!.id, currentOrganization!.id, noteId),

    onMutate: async ({ noteId, liked }) => {
      await queryClient.cancelQueries({ queryKey: notesKeys.all });
      const snapshot = queryClient.getQueriesData({ queryKey: notesKeys.all });

      const me: INoteLike = {
        userId: user?.id ?? "",
        displayName: user?.name ?? null,
        profilePictureUrl: user?.picture ?? null,
      };

      patchCachedNote(queryClient, noteId, (note) => ({
        ...note,
        likedByCurrentUser: !liked,
        likeCount: Math.max(0, (note.likeCount ?? 0) + (liked ? -1 : 1)),
        likes: liked
          ? (note.likes ?? []).filter((l) => l.userId !== user?.id)
          : [...(note.likes ?? []), me],
      }));

      return { snapshot };
    },

    onError: (_error, _variables, context) => {
      context?.snapshot?.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },

    onSuccess: (summary?: INoteLikeSummary) => {
      if (!summary?.noteId) return;
      patchCachedNote(queryClient, summary.noteId, (note) => ({
        ...note,
        likeCount: summary.likeCount,
        likedByCurrentUser: summary.likedByCurrentUser,
        likes: summary.likes,
      }));
    },
  });
}

/**
 * Mutation to create a new note. Invalidates notes and projects queries on success.
 */
export function useCreateNoteMutation() {
  const queryClient = useQueryClient();
  const { tenant, currentOrganization } = useBoundStore();

  return useMutation({
    mutationFn: ({ projectId, note }: { projectId: string; note: { content: string; mentionIds?: string[] } }) =>
      notesService.createNote(tenant!.id, currentOrganization!.id, projectId, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notesKeys.all });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}
